import { Nothing } from 'components/Nothing';
import { FC, useState } from 'react';
import styles from './EmployedsTabletList.module.scss';
import { Group, Loader, Paper, Stack, Text } from '@mantine/core';
import { useNavigate } from 'react-router';
import { AvatarMobile } from 'components/AvatarMobile';
import { getFormatPhone } from 'utils/getFotmatPhone';
import { Employed } from 'redux/Api/employedsApi/type';
import { useDownloadContractMutation } from 'redux/Api/contractsApi/contractsApi';
import { EmployedProblem } from 'pages/EmployedsContent/utils';
import { TextWithTooltip } from 'hooks/TextWithTooltip';
import classNames from 'classnames';
import { EmployedsСategory } from '../EmployedsСategory';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { statusIcons, statusText } from 'ui/EmployedItem';
import { ContractStatus } from 'redux/Api/contractsApi/type';
import { statusColor } from 'components/ContractsListMobile';
import { AppTooltip } from 'ui/AppTooltip';

export interface workerAppTypes {
  app_status: number;
  avatar?: string | null;
  verified: number;
  moderated: number;
  app_exit: number;
  fns_status: number;
}

export interface EmpoyedTypes {
  created_at: string;
  fns_status?: number;
  has_ip: number;
  id: number;
  inn: string;
  last_info_update: string | null;
  name: string;
  nationality: string;
  patronymic: string;
  phone: string;
  surname: string;
  name_invite?: string;
  worker_app: workerAppTypes;
  limit?: number | null;
  limit_updated_at?: string | null;
  limit_updated_by_user?: string | null;
  monthly_payment?: number;
}

interface EmployedsTabletListTypes {
  data: Employed[];
  intersectionComponent: JSX.Element | null;
  setIsCreatedModalOpen: (value: boolean) => void;
}

export const EmployedsTabletList: FC<EmployedsTabletListTypes> = ({
  data,
  intersectionComponent,
  setIsCreatedModalOpen,
}) => {
  const navigate = useNavigate();
  const [downloadingContractId, setDownloadingContractId] = useState<number | null>(null);
  const [downloadContract] = useDownloadContractMutation();
  const reduxEmployeds = useSelector((state: RootState) => state.employeds.items);

  const getEmployedFromRedux = (id: number) => {
    return reduxEmployeds.find((e) => e.id === id);
  };

  const handleDownloadContract = async (id: number | undefined) => {
    if (!id) return;
    setDownloadingContractId(id);
    try {
      const contractId = typeof id === 'string' ? parseInt(id) : id;
      const blob = await downloadContract(contractId).unwrap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `unified_contract_${contractId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('error', error);
    } finally {
      setDownloadingContractId(null);
    }
  };

  const [openCategoryId, setOpenCategoryId] = useState<number | null>(null);

  const handleCategoryToggle = (employedId: number) => {
    setOpenCategoryId((prev) => (prev === employedId ? null : employedId));
  };

  return (
    <Stack align='stretch' justify='center' gap='sm' mb='lg'>
      {data?.length ? (
        data.map((initialItem: Employed) => {
          const item = getEmployedFromRedux(initialItem.id) || initialItem;
          return (
            <Paper
              shadow='0'
              p='sm'
              radius='md'
              key={item.id}
              className={styles.root}
              style={{ cursor: 'pointer' }}
              onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                navigate(`/employees/${item.id}`);
              }}>
              <div className={styles.box}>
                <div className={styles.content}>
                  <div className={styles.nameGroup}>
                    <div className={styles.name}>
                      <AvatarMobile
                        size={36}
                        name={item?.name}
                        surname={item?.surname}
                        avatar={item?.worker_app?.avatar}
                        isDeleted={item?.is_deleted}
                      />

                      <span className={styles.nameTextWrap}>
                        <div className={classNames(styles.name_text)}>
                          <TextWithTooltip
                            content={
                              !item?.surname
                                ? item?.name
                                : item?.patronymic
                                ? `${item?.surname} ${item?.name} ${item?.patronymic}`
                                : `${item?.surname} ${item?.name}`
                            }
                            className={classNames(styles.name_text)}
                            isMobile
                          />
                        </div>
                        {item.inn && <div className={styles.inn}>ИНН {item.inn}</div>}
                      </span>
                    </div>
                  </div>

                  <div className={styles.tagsAndProblem}>
                    <Group gap='8px' wrap='nowrap' align='center'>
                      <Text
                        component='a'
                        href={`tel:+${item.phone}`}
                        fw='400'
                        fz='14px'
                        lh='18px'
                        style={{ whiteSpace: 'nowrap' }}>
                        {getFormatPhone(item.phone)}
                      </Text>
                      {item.worker_app?.nationality && (
                        <Group gap='8px' wrap='nowrap' align='center'>
                          <div className={styles.metaDot} />
                          <Text fw='400' fz='14px' lh='18px' c='#121922'>
                            {item.worker_app?.nationality || ''}
                          </Text>
                        </Group>
                      )}
                      <div className={styles.contractMeta}>
                        <div className={styles.metaDot} />
                        {item.unified_contract ? (
                          <div className={styles.contractStatus}>
                            <div className={styles.contractStatusIcon}>
                              {
                                statusIcons[
                                  item.unified_contract?.status as ContractStatus
                                ]
                              }
                            </div>
                            <div className={styles.statusWithDate_text}>
                              <div
                                className={styles.statusText}
                                style={{
                                  color:
                                    statusColor[
                                      item.unified_contract?.status as ContractStatus
                                    ],
                                }}>
                                <TextWithTooltip
                                  content={
                                    statusText[
                                      item.unified_contract?.status as ContractStatus
                                    ]
                                  }
                                  className={styles.statusTextContent}
                                  isMobile
                                />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className={styles.noContract}>Нет договора</div>
                        )}
                      </div>
                    </Group>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center',
                      marginRight: '60px',
                    }}>
                    {item?.categories?.length > 0 && (
                      <div className={styles.categoryes}>
                        <div className={styles.textCategory}>
                          {item?.categories
                            ?.slice(0, 1)
                            .map((cat) => (
                              <TextWithTooltip key={cat.id} content={cat.name} />
                            ))}
                          {item?.categories?.length > 1 && (
                            <AppTooltip
                              label={
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '4px',
                                  }}>
                                  {item.categories.slice(1).map((cat, idx) => (
                                    <div key={idx}>{cat.name}</div>
                                  ))}
                                </div>
                              }
                              withinPortal={false}
                              openDelay={300}>
                              <p>+{item.categories.length - 1}</p>
                            </AppTooltip>
                          )}
                        </div>
                      </div>
                    )}

                    {item?.categories?.length === 0 ? (
                      <div className={styles.addCategoryButton}>
                        <EmployedsСategory
                          setIsCreatedModalOpen={setIsCreatedModalOpen}
                          isOpen={openCategoryId === item.id}
                          employedId={String(item.id)}
                          onToggle={() => handleCategoryToggle(item.id)}
                          showIcon={true}
                          activeCategories={item?.categories?.map((el) => el.id)}
                          fallbackCategories={item.categories}
                        />
                      </div>
                    ) : (
                      <div
                        className={
                          openCategoryId === item.id
                            ? styles.iconEditActive
                            : styles.iconEdit
                        }>
                        <EmployedsСategory
                          setIsCreatedModalOpen={setIsCreatedModalOpen}
                          isOpen={openCategoryId === item.id}
                          employedId={String(item.id)}
                          onToggle={() => handleCategoryToggle(item.id)}
                          showIcon={false}
                          activeCategories={item?.categories?.map((el) => el.id)}
                          fallbackCategories={item.categories}
                        />
                      </div>
                    )}
                  </div>

                  {item.unified_contract?.status === 'signed_by_worker' && (
                    <Group>
                      <Text
                        fz='16px'
                        lh='22px'
                        c='#002CFB'
                        style={{ cursor: 'pointer' }}
                        onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                          e.stopPropagation();
                          handleDownloadContract(item?.unified_contract?.id);
                        }}>
                        Договор №{item.unified_contract.number}
                      </Text>
                      {downloadingContractId === item.unified_contract?.id && (
                        <Loader size='xs' color='#002CFB' />
                      )}
                    </Group>
                  )}
                </div>
                <div className={styles.problemContainer}>
                  <EmployedProblem employed={item} screen='tablet' />
                </div>
              </div>
            </Paper>
          );
        })
      ) : (
        <Nothing style={{ height: '320px' }} title='Ничего не найдено' />
      )}
      {intersectionComponent}
    </Stack>
  );
};
